import type { AxiosInstance } from 'axios';
import type { ServiceResultT } from '../types/common/ServiceResultT';
import type { ServiceResult } from '../types/common/ServiceResult';
import type { ProductTypeViewModel, ProductTypeCreateModel, ProductTypeUpdateModel } from '../types/product-type';
import type { OrderByDescriptor } from '../types/common/OrderByDescriptor';
import { buildPaginationParams, buildOrderByParams } from '../client/queryParams';
import { apiClient } from '../client/client';
import type { SpecConstraintViewModel } from '../types/product-type/SpecConstraintViewModel';
import type { ProductTypeSpecRule } from '../types/product-type/ProductTypeSpecRule';

export interface ProductTypeQuery {
  name?: string;
  secondaryCategoryId?: number | null;
  isDeleted?: boolean;
  pagination?: { pageSize?: number; pageNumber?: number };
  orderBy?: OrderByDescriptor[] | null;
}

export class ProductTypeService {
  private static readonly BASE = '/api/product-type';

  constructor(private readonly client: AxiosInstance = apiClient) {}

  async getAll(query?: ProductTypeQuery): Promise<ServiceResultT<ProductTypeViewModel[]>> {
    const params: Record<string, unknown> = {};
    if (query?.name) params.Name = query.name;
    if (query?.secondaryCategoryId != null) params.SecondaryCategoryId = query.secondaryCategoryId;
    if (query?.isDeleted != null) params.IsDeleted = query.isDeleted;
    Object.assign(params, buildPaginationParams(query?.pagination));
    Object.assign(params, buildOrderByParams(query?.orderBy));
    const { data } = await this.client.get<ServiceResultT<ProductTypeViewModel[]>>(
      ProductTypeService.BASE,
      { params: Object.keys(params).length ? params : undefined }
    );
    return data;
  }

  async getById(id: number | string): Promise<ServiceResultT<ProductTypeViewModel>> {
    const { data } = await this.client.get<ServiceResultT<ProductTypeViewModel>>(
      `${ProductTypeService.BASE}/${id}`
    );
    return data;
  }

  async create(payload: ProductTypeCreateModel): Promise<ServiceResultT<ProductTypeViewModel>> {
    const { data } = await this.client.post<ServiceResultT<ProductTypeViewModel>>(
      ProductTypeService.BASE,
      payload
    );
    return data;
  }

  async update(id: number | string, payload: ProductTypeUpdateModel): Promise<ServiceResultT<ProductTypeViewModel>> {
    const { data } = await this.client.put<ServiceResultT<ProductTypeViewModel>>(
      `${ProductTypeService.BASE}/${id}`,
      payload
    );
    return data;
  }

  async delete(id: number | string): Promise<ServiceResult> {
    const { data } = await this.client.delete<ServiceResult>(`${ProductTypeService.BASE}/${id}`);
    return data;
  }

  async getSpecConstraints(productTypeId: number): Promise<ServiceResultT<SpecConstraintViewModel[]>> {
    const { data } = await this.client.get<ServiceResultT<SpecConstraintViewModel[]>>(
      `${ProductTypeService.BASE}/${productTypeId}/constraints`
    );
    return data;
  }

  async getRules(productTypeId: number): Promise<ServiceResultT<ProductTypeSpecRule[]>> {
    const { data } = await this.client.get<ServiceResultT<ProductTypeSpecRule[]>>(
      `${ProductTypeService.BASE}/${productTypeId}/rules`
    );
    return data;
  }

  async updateRules(
    productTypeId: number,
    rules: ProductTypeSpecRule[]
  ): Promise<ServiceResultT<ProductTypeSpecRule[]>> {
    const { data } = await this.client.put<ServiceResultT<ProductTypeSpecRule[]>>(
      `${ProductTypeService.BASE}/${productTypeId}/rules`,
      { rules }
    );
    return data;
  }
}

export const productTypeService = new ProductTypeService();
